<script setup lang="ts">
import { computed, ref } from "vue";
import BaseButton from "./BaseButton.vue";
import BaseIcon from "./BaseIcon.vue";
import BasePopover from "./BasePopover.vue";

export type PopoverPlacement =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end";

export type PopoverHasPopup = "menu" | "listbox" | "tree" | "grid" | "dialog" | "true";

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<{
  visible: boolean;
  variant?:
    | "primary"
    | "primary-glass"
    | "secondary"
    | "danger"
    | "danger-solid"
    | "danger-glass"
    | "outline"
    | "ghost"
    | "accent-ghost"
    | "accent-outline"
    | "info"
    | "info-solid"
    | "success"
    | "success-solid"
    | "warning"
    | "transparent";
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  label?: string;
  loadingLabel?: string;
  block?: boolean;
  rounded?: boolean;
  align?: "start" | "center" | "end";
  display?: "inline-flex" | "flex" | "grid";
  showChevron?: boolean;
  chevronIcon?: string;
  chevronSize?: number;
  chevronRotateOn?: "open" | "closed";
  hasPopup?: PopoverHasPopup;
  placement?: PopoverPlacement;
  offset?: number;
  teleport?: boolean;
  popoverWidth?: string;
  popoverClassName?: string;
  popoverPadding?: string;
  closeOnClickOutside?: boolean;
  closeOnScroll?: boolean;
  flip?: boolean;
  matchTriggerWidth?: boolean;
  minWidthPx?: number;
  constrainHeight?: boolean;
}>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
  block: false,
  rounded: false,
  align: "center",
  display: "inline-flex",
  loadingLabel: "Loading",
  showChevron: true,
  chevronIcon: "ChevronDown",
  chevronSize: 15,
  chevronRotateOn: "open",
  placement: "bottom",
  offset: 8,
  teleport: true,
  popoverWidth: "w-56",
  popoverClassName: "",
  popoverPadding: "py-2",
  closeOnClickOutside: true,
  closeOnScroll: false,
  flip: true,
  matchTriggerWidth: false,
  constrainHeight: false,
});

const emit = defineEmits<{
  "update:visible": [value: boolean];
  click: [payload: MouseEvent];
  open: [];
  close: [];
}>();

const triggerComponent = ref<{ $el?: unknown } | null>(null);
const triggerRef = computed<HTMLElement | null>(() => {
  const element = triggerComponent.value?.$el;
  return element instanceof HTMLElement ? element : null;
});

const resolvedPlacement = ref<PopoverPlacement>(props.placement);
const chevronPlacement = computed(() => props.visible ? resolvedPlacement.value : props.placement);

const chevronClass = computed(() => ({
  "rotate-180": chevronPlacement.value.startsWith("top")
    ? props.chevronRotateOn === "open" ? !props.visible : props.visible
    : props.chevronRotateOn === "open" ? props.visible : !props.visible,
}));

function setVisible(value: boolean): void {
  if (value === props.visible) return;
  emit("update:visible", value);
  if (value) emit("open");
  else emit("close");
}

function handleClick(event: MouseEvent): void {
  emit("click", event);
  setVisible(!props.visible);
}

function close(): void {
  setVisible(false);
}

defineExpose({ close, triggerRef });
</script>

<template>
  <BaseButton
    ref="triggerComponent"
    v-bind="$attrs"
    :variant="variant"
    :size="size"
    :type="type"
    :disabled="disabled"
    :loading="loading"
    :icon="icon"
    :label="label"
    :loading-label="loadingLabel"
    :block="block"
    :rounded="rounded"
    :align="align"
    :display="display"
    :aria-haspopup="hasPopup"
    :aria-expanded="visible"
    @click="handleClick"
  >
    <template v-if="$slots.default" #default>
      <slot />
    </template>
    <template #end>
      <slot name="end" />
      <BaseIcon
        v-if="showChevron"
        :name="chevronIcon"
        :size="chevronSize"
        class="ml-auto transition-transform duration-200"
        :class="chevronClass"
        decorative
      />
    </template>
  </BaseButton>

  <BasePopover
    :visible="visible"
    @update:visible="setVisible"
    @update:resolved-placement="resolvedPlacement = $event"
    :trigger-ref="triggerRef"
    :placement="placement"
    :offset="offset"
    :teleport="teleport"
    :width="popoverWidth"
    :class-name="popoverClassName"
    :padding="popoverPadding"
    :close-on-click-outside="closeOnClickOutside"
    :close-on-scroll="closeOnScroll"
    :flip="flip"
    :match-trigger-width="matchTriggerWidth"
    :min-width-px="minWidthPx"
    :constrain-height="constrainHeight"
  >
    <slot name="popover" />
  </BasePopover>
</template>
